import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import AdminPayoutStatusModal from './AdminPayoutStatusModal';

interface Transaction {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  transaction_type: 'CREDIT' | 'DEBIT' | 'wallet_credit' | 'payout_debit' | 'escrow_release' | 'WALLET_CREDIT' | 'WALLET_DEBIT' | 'wallet_debit';
  amount: number;
  commission_amount: number;
  description: string;
  reference_id: string;
  status: string;
  payment_details?: any;
  recipient_details?: any;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  created_by_email?: string;
  created_by_role?: string;
  created_by?: number;
  user_wallet_balance?: number;
  balance_before?: number;
  balance_after?: number;
  manager_wallet_balance?: number;
  manager_balance_at_transaction?: number;
}

interface Payout {
  id: number;
  merchant_id: number;
  merchant_reference_number: string;
  viyona_transaction_id?: string;
  account_name: string;
  account_number: string;
  amount: number;
  net_amount?: number;
  transaction_fees_percentage?: number;
  transaction_fee_amount?: number;
  ifsc_code: string;
  bank_name: string;
  bank_branch?: string;
  transfer_type: 'IMPS' | 'NEFT' | 'RTGS';
  customer_email?: string;
  customer_phone?: string;
  remark?: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  error_message?: string;
  bank_reference_number?: string;
  transaction_date?: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  current_wallet_balance?: number;
}

const ReportManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'managers' | 'merchants'>('managers');
  const [managerTransactions, setManagerTransactions] = useState<Transaction[]>([]);
  const [merchantTransactions, setMerchantTransactions] = useState<Payout[]>([]);
  const [merchantSummary, setMerchantSummary] = useState({
    totalPayoutAmount: 0,
    totalPayoutCount: 0,
    totalServiceFee: 0,
  });
  const [loading, setLoading] = useState(false);
  
  // Pagination
  const [managersPage, setManagersPage] = useState(1);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [managersTotalPages, setManagersTotalPages] = useState(1);
  const [merchantsTotalPages, setMerchantsTotalPages] = useState(1);
  const [managersTotalRecords, setManagersTotalRecords] = useState(0);
  const limit = 20;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [payoutDetails, setPayoutDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Status Modal state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatusPayout, setSelectedStatusPayout] = useState<Payout | null>(null);
  const [statusDetails, setStatusDetails] = useState<any>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Search and filters
  const [managersSearch, setManagersSearch] = useState('');
  const [merchantsSearch, setMerchantsSearch] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'ALL' | 'CREDIT' | 'DEBIT'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'COMPLETED' | 'PENDING' | 'FAILED'>('ALL');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchManagerTransactions();
  }, [managersPage, managersSearch, transactionTypeFilter, statusFilter, dateRange]);

  useEffect(() => {
    fetchMerchantTransactions();
  }, [merchantsPage, merchantsSearch, transactionTypeFilter, statusFilter, dateRange]);

  // Fetch global merchant totals once (independent of filters/pagination)
  useEffect(() => {
    fetchMerchantTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchManagerTransactions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTransactions({
        page: managersPage,
        limit,
        search: managersSearch,
        transaction_type: transactionTypeFilter === 'ALL' ? undefined : transactionTypeFilter,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        start_date: dateRange.startDate || undefined,
        end_date: dateRange.endDate || undefined,
        manager_sent_money: 'true' // Show only transactions where manager sent money
      });
      
      console.log('Manager API Response:', response.data);
      console.log('Manager transactions:', response.data.data.transactions);
      
      // Debug manager wallet balance
      if (response.data.data.transactions.length > 0) {
        const firstTransaction = response.data.data.transactions[0];
        console.log('🔍 First Transaction Debug:');
        console.log('  - ID:', firstTransaction.id);
        console.log('  - Created by:', firstTransaction.created_by_name, '(ID:', firstTransaction.created_by, ')');
        console.log('  - Manager wallet balance:', firstTransaction.manager_wallet_balance);
        console.log('  - Manager balance at transaction:', firstTransaction.manager_balance_at_transaction);
        console.log('  - Amount:', firstTransaction.amount);
        console.log('  - Transaction type:', firstTransaction.transaction_type);
      }
      
      setManagerTransactions(response.data.data.transactions);
      setManagersTotalPages(response.data.data.pagination.totalPages);
      setManagersTotalRecords(response.data.data.pagination.totalRecords);
    } catch (error) {
      console.error('Error fetching manager transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantTransactions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllPayouts({
        page: merchantsPage,
        limit,
        search: merchantsSearch,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        date_from: dateRange.startDate || undefined,
        date_to: dateRange.endDate || undefined
      });
      
      console.log('Payouts API Response:', response.data);
      console.log('Payouts:', response.data.data.payouts);
      setMerchantTransactions(response.data.data.payouts);
      setMerchantsTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Independent totals for Merchant Transfers summary cards
  const fetchMerchantTotals = async () => {
    try {
      const response = await adminAPI.getAllPayouts({
        page: 1,
        limit: 10000, // fetch enough rows to compute global totals
      });

      const payouts: Payout[] = response.data?.data?.payouts || [];
      const totalAmount = payouts.reduce((sum, p) => sum + Number(p.amount || 0), 0);
      const totalFee = payouts.reduce((sum, p) => sum + Number(p.transaction_fee_amount || 0), 0);

      setMerchantSummary({
        totalPayoutAmount: totalAmount,
        totalPayoutCount: payouts.length,
        totalServiceFee: totalFee,
      });
    } catch (error) {
      console.error('Error fetching merchant totals:', error);
      setMerchantSummary({ totalPayoutAmount: 0, totalPayoutCount: 0, totalServiceFee: 0 });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionTypeIcon = (type: string) => {
    const upperType = type.toUpperCase();
    return (upperType === 'CREDIT' || upperType === 'WALLET_CREDIT' || upperType.includes('CREDIT')) ? '💰' : '💸';
  };

  const getTransactionTypeColor = (type: string) => {
    // Convert to uppercase for consistent comparison
    const upperType = type.toUpperCase();
    return (upperType === 'CREDIT' || upperType === 'WALLET_CREDIT' || upperType.includes('CREDIT')) ? 'credit' : 'debit';
  };

  const getTransactionDestination = (transaction: Transaction) => {
    // For manager transfers, the destination is the merchant who received the money
    // This is shown in the user_name and user_email fields
    if (transaction.user_name && transaction.user_email) {
      return `${transaction.user_name} (${transaction.user_email})`;
    }

    // Try to get destination from recipient_details or payment_details
    if (transaction.recipient_details) {
      try {
        const details = typeof transaction.recipient_details === 'string' 
          ? JSON.parse(transaction.recipient_details) 
          : transaction.recipient_details;
        
        if (details.name) {
          return `${details.name} (${details.email || details.id || 'Unknown'})`;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    if (transaction.payment_details) {
      try {
        const details = typeof transaction.payment_details === 'string' 
          ? JSON.parse(transaction.payment_details) 
          : transaction.payment_details;
        
        if (details.recipient_name) {
          return `${details.recipient_name} (${details.recipient_email || details.recipient_id || 'Unknown'})`;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    // Fallback based on transaction type and user role
    if (transaction.transaction_type === 'CREDIT') {
      if (transaction.user_role === 'manager') {
        return 'Manager Wallet';
      } else if (transaction.user_role === 'merchant') {
        return 'Merchant Wallet';
      }
    } else if (transaction.transaction_type === 'DEBIT') {
      if (transaction.user_role === 'manager') {
        return 'System/Commission';
      } else if (transaction.user_role === 'merchant') {
        return 'Payout/Transfer';
      }
    }

    return 'Unknown Destination';
  };


  const renderTransactionTable = (transactions: Transaction[] | Payout[], type: 'managers' | 'merchants') => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {type} transactions...</p>
        </div>
      );
    }

    if (transactions.length === 0) {
      return (
        <div className="empty-state">
          <span className="empty-icon">📊</span>
          <h3>No transactions found</h3>
          <p>No {type} transactions match your current filters</p>
        </div>
      );
    }

    // Enhanced view for Managers only
    if (type === 'managers') {
      const managerTransactions = transactions as Transaction[];
      return (
        <div className="table-container">
          <table className="transaction-table managers-table">
            <thead>
              <tr>
                <th>Serial No</th>
                <th>Date</th>
                <th>From Manager</th> 
                <th>Send To</th>
                <th>Manager Wallet Balance</th>
                <th>Amount Sent</th>
                <th>Merchant Wallet Balance</th> 
                <th>Transaction Type</th> 
                <th>Description</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {managerTransactions.map((transaction, index) => (
                <tr key={transaction.id}>
                  <td className="serial-number">{index + 1}</td>
                  <td className="date">{formatDate(transaction.created_at)}</td>
                  <td>
                    <div className="user-info">
                      <strong>{transaction.created_by_name || 'Unknown'}</strong>
                      <span className="user-email">{transaction.created_by_email || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="to-details">
                      {getTransactionDestination(transaction)}
                    </span>
                  </td>
                  <td className="wallet-balance">
                    <div className="balance-cell">
                      {(() => {
                        // Prioritize manager_wallet_balance since it has the correct value
                        const managerBalance = transaction.manager_wallet_balance || transaction.manager_balance_at_transaction || 0;
                        console.log(`🔍 Transaction ${transaction.id} Manager Balance:`, {
                          manager_balance_at_transaction: transaction.manager_balance_at_transaction,
                          manager_wallet_balance: transaction.manager_wallet_balance,
                          final_balance: managerBalance
                        });
                        return (
                          <>
                            <span className="balance-amount">{formatCurrency(managerBalance)}</span>
                            <span className="balance-label">Manager Wallet</span>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className={`amount ${getTransactionTypeColor(transaction.transaction_type)}-amount`}>
                    <span className="amount-symbol">{getTransactionTypeColor(transaction.transaction_type) === 'credit' ? '+' : '-'}</span>
                    <span className={`amount-text ${getTransactionTypeColor(transaction.transaction_type)}-text`}>
                      {formatCurrency(transaction.amount)}
                    </span>
                  </td>
                  <td className="wallet-balance">
                    <div className="balance-cell"> 
                     <span className="balance-amount">{formatCurrency(transaction.balance_before || 0)}</span>
                     <span className="balance-label">Before Transaction</span> 
                      <span className="balance-amount">{formatCurrency(transaction.balance_after || 0)}</span>
                      <span className="balance-label">After Transaction</span>
                    </div>
                  </td>
                  <td>
                    <span className={`transaction-type ${getTransactionTypeColor(transaction.transaction_type)}`}>
                      {getTransactionTypeIcon(transaction.transaction_type)} {transaction.transaction_type}
                    </span>
                  </td>
                  <td className="description">{transaction.description}</td>
                  <td>
                    <span className={`status-badge ${transaction.status.toLowerCase()}`}>
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Simple view for Merchants (Payouts)
    const payouts = transactions as Payout[];
    return (
      <div className="table-container">
        <table className="transaction-table merchants-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>MERCHANT NAME</th>
              <th>BENEFICIARY DETAILS</th>
              <th>AMOUNT</th>
              <th>CURRENT WALLET BALANCE</th>
              <th>TRANSFER TYPE</th>
              <th>MERCHANT REFERENCE NUMBER</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map(payout => (
              <tr key={payout.id}>
                <td className="date">{formatDate(payout.created_at)}</td>
                <td>
                  <div className="user-info">
                    <strong>{payout.user_name || 'N/A'}</strong>
                    <span className="user-email">{payout.user_email || 'N/A'}</span>
                  </div>
                </td>
                <td>
                  <div className="account-info">
                    <strong>{payout.account_name}</strong>
                    <span className="account-number">{payout.account_number}</span>
                    <span className="bank-name">{payout.bank_name}</span>
                  </div>
                </td>
                <td className="amount">{formatCurrency(payout.amount)}</td>
                <td className="wallet-balance">{formatCurrency(payout.current_wallet_balance || 0)}</td>
                <td>
                  <span className={`transfer-type ${payout.transfer_type.toLowerCase()}`}>
                    {payout.transfer_type}
                  </span>
                </td>
                <td>
                  <span className={`merchant-reference-number ${payout.merchant_reference_number.toLowerCase()}`}>
                    {payout.merchant_reference_number}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${payout.status.toLowerCase()}`}>
                    {payout.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon btn-info"
                      onClick={() => handleViewDetails(payout)}
                      title="View Details"
                    >
                      👁️
                    </button>
                    <button 
                      className="btn-icon btn-warning"
                      onClick={() => handleCheckStatus(payout)}
                      title="Check Status"
                    >
                      🔍
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  const getTotalAmount = (transactions: Transaction[], type: 'CREDIT' | 'DEBIT') => {
    return transactions
      .filter(t => t.transaction_type === type)
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  };

  // Specific functions for each summary card - Manager Transfers only
  const getTotalCredit = (transactions: Transaction[]) => {
    // Manager credit transactions: wallet_credit and WALLET_CREDIT (manager sending money to merchants)
    const creditTransactions = transactions.filter(t => 
      t.transaction_type === 'wallet_credit' || t.transaction_type === 'WALLET_CREDIT'
    );
    const total = creditTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalCredit (Manager):', { transactionCount: creditTransactions.length, total, transactions: creditTransactions });
    return total;
  };

  const getTotalDebit = (transactions: Transaction[]) => {
    // Manager debit transactions: all wallet_debit, WALLET_DEBIT and DEBIT transactions
    const debitTransactions = transactions.filter(t => 
      t.transaction_type === 'DEBIT' || 
      t.transaction_type === 'WALLET_DEBIT' || 
      t.transaction_type === 'wallet_debit'
    );
    const total = debitTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalDebit (Manager):', { transactionCount: debitTransactions.length, total, transactions: debitTransactions });
    return total;
  };

  const getTotalDebitCommission = (transactions: Transaction[]) => {
    // Manager commission transactions: any debit with commission/fee
    const commissionTransactions = transactions.filter(t => 
      (t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit') && 
      (t.description.toLowerCase().includes('commission') || t.description.toLowerCase().includes('fee'))
    );
    const total = commissionTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalDebitCommission (Manager):', { transactionCount: commissionTransactions.length, total, transactions: commissionTransactions });
    return total;
  };

  const getTotalEscrowCredit = (transactions: Transaction[]) => {
    // Manager escrow credit transactions: escrow_release
    const escrowCreditTransactions = transactions.filter(t => 
      t.transaction_type === 'escrow_release'
    );
    const total = escrowCreditTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalEscrowCredit (Manager):', { transactionCount: escrowCreditTransactions.length, total, transactions: escrowCreditTransactions });
    return total;
  };

  const getTotalEscrowDebit = (transactions: Transaction[]) => {
    // Manager escrow debit transactions: any debit with escrow
    const escrowDebitTransactions = transactions.filter(t => 
      (t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit') && 
      t.description.toLowerCase().includes('escrow')
    );
    const total = escrowDebitTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalEscrowDebit (Manager):', { transactionCount: escrowDebitTransactions.length, total, transactions: escrowDebitTransactions });
    return total;
  };

  const getTotalServiceFee = (transactions: Transaction[]) => {
    // Total service fee: sum of all commission amounts
    const total = transactions.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0);
    console.log('getTotalServiceFee (Manager):', { transactionCount: transactions.length, total });
    return total;
  };

  // Manager transaction counts (dynamic summary cards)
  const getManagerCreditCount = (transactions: Transaction[]) => {
    return transactions.filter(t => t.transaction_type === 'wallet_credit' || t.transaction_type === 'WALLET_CREDIT').length;
  };

  const getManagerDebitCount = (transactions: Transaction[]) => {
    return transactions.filter(t => t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit').length;
  };

  // Merchant-specific calculation functions
  const getMerchantTotalCredit = (payouts: Payout[]) => {
    // Merchant payout amount: sum of all payout amounts
    const total = payouts.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    console.log('getMerchantTotalCredit (Payouts):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalDebit = (payouts: Payout[]) => {
    // Merchant payout count: count of payouts
    const total = payouts.length;
    console.log('getMerchantTotalDebit (Payout Count):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalDebitCommission = (payouts: Payout[]) => {
    // Merchant service fee: sum of transaction_fee_amount from payouts
    const total = payouts.reduce((sum, p) => sum + Number(p.transaction_fee_amount || 0), 0);
    console.log('getMerchantTotalDebitCommission (Service Fee):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalServiceFee = (payouts: Payout[]) => {
    // Total service fee for merchants: sum of all transaction_fee_amount from payouts
    const total = payouts.reduce((sum, p) => sum + Number(p.transaction_fee_amount || 0), 0);
    console.log('getMerchantTotalServiceFee:', { payoutCount: payouts.length, total });
    return total;
  };

  const getTotalTransactions = (transactions: Transaction[]) => {
    return transactions.length;
  };

  const getTotalPayouts = (payouts: Payout[]) => {
    return payouts.length;
  };

  // Function to fetch payout details
  const fetchPayoutDetails = async (merchantReferenceNumber: string) => {
    try {
      setLoadingDetails(true);
      console.log('Fetching details for merchant reference:', merchantReferenceNumber);
      
      // Make API call to get payout details
      const response = await adminAPI.getPayoutDetails(merchantReferenceNumber);
      console.log('Payout details response:', response.data);
      
      setPayoutDetails(response.data);
    } catch (error) {
      console.error('Error fetching payout details:', error);
      setPayoutDetails({ error: 'Failed to fetch payout details' });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Function to fetch payout status details from ViyonaPay
  const fetchPayoutStatusDetails = async (merchantReferenceNumber: string) => {
    try {
      setLoadingStatus(true);
      console.log('Fetching live status from ViyonaPay for reference:', merchantReferenceNumber);
      
      // Make API call to get live status from ViyonaPay using admin endpoint
      const response = await adminAPI.checkPayoutStatus(merchantReferenceNumber);
      console.log('ViyonaPay status response:', response.data);
      
      // Handle admin endpoint response format
      if (response.data && response.data.success) {
        console.log('Admin API returned successful response:', response.data);
        setStatusDetails(response.data);
      } else {
        console.log('Admin API returned error response:', response.data);
        setStatusDetails({ error: response.data.message || 'Failed to fetch status' });
      }
    } catch (error: any) {
      console.error('Error fetching ViyonaPay status:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract the actual error message from the API response
      let errorMessage = 'Failed to fetch live status from ViyonaPay';
      
      console.log('Full error object:', error);
      console.log('Error response:', error.response);
      console.log('Error response data:', error.response?.data);
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        console.log('Using error.response.data.message:', errorMessage);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        console.log('Using error.response.data.error:', errorMessage);
      } else if (error.message) {
        errorMessage = error.message;
        console.log('Using error.message:', errorMessage);
      }
      
      console.log('Final error message:', errorMessage);
      setStatusDetails({ error: errorMessage });
    } finally {
      setLoadingStatus(false);
    }
  };

  // Function to open modal and fetch details
  const handleViewDetails = (payout: Payout) => {
    setSelectedPayout(payout);
    setIsModalOpen(true);
    fetchPayoutDetails(payout.merchant_reference_number);
  };

  // Function to check payout status
  const handleCheckStatus = (payout: Payout) => {
    setSelectedStatusPayout(payout);
    setIsStatusModalOpen(true);
    fetchPayoutStatusDetails(payout.merchant_reference_number);
  };

  // Function to close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPayout(null);
    setPayoutDetails(null);
  };

  // Function to close status modal
  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedStatusPayout(null);
    setStatusDetails(null);
  };

  return (
    <div className="report-management">
      <div className="page-header">
        <div className="header-content">
          <h1>📊 Report Management</h1>
          <p>View Manager money transfers and Merchant transactions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'managers' ? 'active' : ''}`}
          onClick={() => setActiveTab('managers')}
        >
          <span className="tab-icon">👔</span>
          <span className="tab-label">Manager Transfers</span>
          <span className="tab-count">{managersTotalRecords}</span>
        </button>
        <button
          className={`tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <span className="tab-icon">🏪</span>
          <span className="tab-label">Merchant Transfers</span>
          <span className="tab-count">{merchantSummary.totalPayoutCount}</span>
        </button>
      </div>

      {/* Section Header */}
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
          {activeTab === 'managers' ? 'Manager Transfers' : 'Merchant Transfers'}
        </h2>
        <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)' }}>
          {activeTab === 'managers' 
            ? 'View all money transfers initiated by managers to merchants' 
            : 'View all merchant payout transactions'
          }
        </p>
      </div>

      {/* Summary Cards - move Merchant cards above filters as requested */}
      {activeTab === 'merchants' && (
        <div className="summary-cards">
          <div className="summary-card credit">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>TOTAL PAYOUT AMOUNT</h3>
              <p className="card-value">{formatCurrency(merchantSummary.totalPayoutAmount)}</p>
            </div>
          </div>
          <div className="summary-card credit">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>TOTAL PAYOUT COUNT</h3>
              <p className="card-value">{merchantSummary.totalPayoutCount}</p>
            </div>
          </div>
          <div className="summary-card credit"> 
            <div className="card-icon">💸</div>
            <div className="card-content">
              <h3>TOTAL SERVICE FEE</h3> 
              <p className="card-value">{formatCurrency(merchantSummary.totalServiceFee)}</p>
            </div> 
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          {activeTab === 'managers' && (
            <div className="filter-group">
              <label>Transaction Type:</label>
              <select
                value={transactionTypeFilter}
                onChange={(e) => setTransactionTypeFilter(e.target.value as 'ALL' | 'CREDIT' | 'DEBIT')}
                className="filter-select"
              >
                <option value="ALL">All Transactions</option>
                <option value="CREDIT">Credits Only</option>
                <option value="DEBIT">Debits Only</option>
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'COMPLETED' | 'PENDING' | 'FAILED')}
              className="filter-select"
            >
              <option value="ALL">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <button
            className="btn-secondary"
            onClick={() => {
              setDateRange({ startDate: '', endDate: '' });
              if (activeTab === 'managers') {
                setTransactionTypeFilter('ALL');
              }
              setStatusFilter('ALL');
            }}
          >
            Clear Filters
          </button>
          
          {/* Search Input */}
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              className="search-input"
              placeholder={`Search ${activeTab} transactions...`}
              value={activeTab === 'managers' ? managersSearch : merchantsSearch}
              onChange={(e) => {
                if (activeTab === 'managers') {
                  setManagersSearch(e.target.value);
                  setManagersPage(1);
                } else {
                  setMerchantsSearch(e.target.value);
                  setMerchantsPage(1);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {activeTab === 'managers' && (
        <div className="summary-cards">
          <div className="summary-card credit">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>TOTAL CREDIT</h3>
              <p className="card-value">{formatCurrency(getTotalCredit(managerTransactions))}</p>
             
            </div>
          </div>
          <div className="summary-card debit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL DEBIT</h3>
              <p className="card-value">{formatCurrency(getTotalDebit(managerTransactions))}</p>
             
            </div>
          </div>

          <div className="summary-card credit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL CREDIT TRANSACTIONS</h3>
              <p className="card-value">{getManagerCreditCount(managerTransactions)}</p>
            </div> 
          </div>

           <div className="summary-card credit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL DEBIT TRANSACTIONS</h3>
              <p className="card-value">{getManagerDebitCount(managerTransactions)}</p>
            </div> 
          </div>  
          
          
        </div>
      )}


      {/* Transaction Tables */}
      {activeTab === 'managers' && (
        <>
          {renderTransactionTable(managerTransactions, 'managers')}
          {renderPagination(managersPage, managersTotalPages, setManagersPage)}
        </>
      )}

      {activeTab === 'merchants' && (
        <>
          {renderTransactionTable(merchantTransactions, 'merchants')}
          {renderPagination(merchantsPage, merchantsTotalPages, setMerchantsPage)}
        </>
      )}

      {/* Payout Details Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payout Details</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              {loadingDetails ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading payout details...</p>
                </div>
              ) : payoutDetails ? (
                <div className="payout-details">
                  {payoutDetails.error ? (
                    <div className="error-message">
                      <p>❌ {payoutDetails.error}</p>
                    </div>
                  ) : (
                    <div className="details-grid">
                      <div className="detail-item">
                        <label>Merchant Reference:</label>
                        <span>{selectedPayout?.merchant_reference_number}</span>
                      </div>
                      <div className="detail-item">
                        <label>Amount:</label>
                        <span>{formatCurrency(selectedPayout?.amount || 0)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Status:</label>
                        <span className={`status-badge ${selectedPayout?.status.toLowerCase()}`}>
                          {selectedPayout?.status}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Account Name:</label>
                        <span>{selectedPayout?.account_name}</span>
                      </div>
                      <div className="detail-item">
                        <label>Account Number:</label>
                        <span>{selectedPayout?.account_number}</span>
                      </div>
                      <div className="detail-item">
                        <label>IFSC Code:</label>
                        <span>{selectedPayout?.ifsc_code}</span>
                      </div>
                      <div className="detail-item">
                        <label>Bank Name:</label>
                        <span>{selectedPayout?.bank_name}</span>
                      </div>
                      <div className="detail-item">
                        <label>Transfer Type:</label>
                        <span>{selectedPayout?.transfer_type}</span>
                      </div>
                      <div className="detail-item">
                        <label>Created At:</label>
                        <span>{formatDate(selectedPayout?.created_at || '')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Payout Status Modal */}
      <AdminPayoutStatusModal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        statusData={statusDetails}
        loading={loadingStatus}
      />
    </div>
  );
};

export default ReportManagement;
